import { curry, pathOr } from 'ramda'
import { generateContext } from '@btime/react-contexts'
import React, { useCallback, useContext, useReducer } from 'react'
import { v4 as uuid } from 'uuid'
import { useState } from 'react'
import { useChecklistFormattedFormData } from './formatChecklistFormData'

import reducer, {
  ADD_CATEGORY,
  ADD_CHILD_CONDITIONS_ON_SELECTED_QUESTION,
  ADD_QUESTION_CLOSE_TO,
  ADD_QUESTION_TO_PARENT,
  DUPLICATE_CATEGORY,
  DUPLICATE_SELECTED_QUESTION,
  EDIT_CHILD_CONDITION_ON_SELECTED_QUESTION,
  MOVE_ELEMENT_CLOSE_TO,
  MOVE_QUESTION_TO_PARENT,
  REMOVE_CATEGORY,
  REMOVE_CHILD_CONDITION_ON_SELECTED_QUESTION,
  REMOVE_SELECTED_QUESTION,
  SELECT_QUESTION,
  SET_CONDITIONS_MODAL_OPEN,
  SET_TRIGGERS_QUESTION_CHECK_MODAL_OPEN,
  SET_SELECTED_CATEGORY,
  SET_SELECTED_QUESTION_VALUE,
  SET_VALUE,
  UNDO_VALUE,
  initialState,
  SET_SELECTED_SEGWARE_TEST_MODE_VALUE,
  SET_TRIGGERS_CATEGORY_CHECK_MODAL_OPEN
} from './reducer'

const { StateContext, ActionsContext, Provider } = generateContext()
export const useChecklistForm = () => useContext(StateContext)
export const useChecklistFormActions = () => useContext(ActionsContext)

export const useIsEditing = () => {
  const form = useChecklistForm()
  return !!form && !!form.id
}

export const useSelectedQuestion = () => {
  const form = useChecklistForm()
  const fullPath = form.selectedQuestionPath
  return pathOr({}, fullPath, form)
}

const Form = ({ children }) => {
  const checklistFormData = useChecklistFormattedFormData()
  const [state, dispatch] = useReducer(reducer, { ...initialState, ...(checklistFormData || {}) })
  const [randomIdAfterUndo, setRandomIdAfterUndo] = useState('')

  const canUndo = state.history?.length > 0 || false

  const handleUndo = useCallback(() => {
    dispatch({ type: UNDO_VALUE })
    setRandomIdAfterUndo(uuid())
  }, [dispatch])

  const handleChange = useCallback(
    curry((field, value) => dispatch({ type: SET_VALUE, payload: { field, value } })),
    [dispatch]
  )

  const handleSelectedSegwareTestChange = useCallback(
    curry((field, value) => dispatch({ type: SET_SELECTED_SEGWARE_TEST_MODE_VALUE, payload: { field, value } })),
    [dispatch]
  )

  const handleSelectedQuestionChange = useCallback(
    curry((field, value) => dispatch({ type: SET_SELECTED_QUESTION_VALUE, payload: { path: [field], value } })),
    [dispatch]
  )

  const handleSelectedQuestionChangeOnPath = useCallback(
    curry((path, value) => dispatch({ type: SET_SELECTED_QUESTION_VALUE, payload: { path, value } })),
    [dispatch]
  )

  const setSelectedCategory = useCallback(
    (index, value) => dispatch({ type: SET_SELECTED_CATEGORY, payload: { index, value } }),
    [dispatch]
  )

  const duplicateSelectedQuestion = useCallback(() => dispatch({ type: DUPLICATE_SELECTED_QUESTION }), [dispatch])
  const removeSelectedQuestion = useCallback(() => dispatch({ type: REMOVE_SELECTED_QUESTION }), [dispatch])

  const addQuestionToParent = useCallback(
    (parentReference, question) => dispatch({ type: ADD_QUESTION_TO_PARENT, payload: { parentReference, question } }),
    [dispatch]
  )

  const addQuestionCloseTo = useCallback(
    (toReference, question, direction) =>
      dispatch({ type: ADD_QUESTION_CLOSE_TO, payload: { toReference, question, direction } }),
    [dispatch]
  )

  const moveElementCloseTo = useCallback(
    (fromReference, toReference, direction) =>
      dispatch({ type: MOVE_ELEMENT_CLOSE_TO, payload: { fromReference, toReference, direction } }),
    [dispatch]
  )

  const moveQuestionToParent = useCallback(
    (fromReference, parentReference, displayConditions) =>
      dispatch({ type: MOVE_QUESTION_TO_PARENT, payload: { fromReference, parentReference, displayConditions } }),
    [dispatch]
  )

  const selectQuestion = useCallback(payload => dispatch({ type: SELECT_QUESTION, payload }), [dispatch])

  const addCategory = useCallback(() => dispatch({ type: ADD_CATEGORY }), [dispatch])

  const duplicateCategory = useCallback(index => dispatch({ type: DUPLICATE_CATEGORY, payload: index }), [dispatch])
  const removeCategory = useCallback(index => dispatch({ type: REMOVE_CATEGORY, payload: index }), [dispatch])

  const addChildConditionsToSelectedQuestion = useCallback(
    conditions => dispatch({ type: ADD_CHILD_CONDITIONS_ON_SELECTED_QUESTION, payload: conditions }),
    [dispatch]
  )

  const editChildConditionToSelectedQuestion = useCallback(
    conditions => dispatch({ type: EDIT_CHILD_CONDITION_ON_SELECTED_QUESTION, payload: conditions }),
    [dispatch]
  )

  const removeChildConditionToSelectedQuestion = useCallback(
    index => dispatch({ type: REMOVE_CHILD_CONDITION_ON_SELECTED_QUESTION, payload: index }),
    [dispatch]
  )

  const openConditionsModal = useCallback(
    index => dispatch({ type: SET_CONDITIONS_MODAL_OPEN, payload: { open: true, index } }),
    [dispatch]
  )

  const closeConditionsModal = useCallback(
    () => dispatch({ type: SET_CONDITIONS_MODAL_OPEN, payload: { open: false, index: null } }),
    [dispatch]
  )

  const openTriggersQuestionCheckModal = useCallback(
    () => dispatch({ type: SET_TRIGGERS_QUESTION_CHECK_MODAL_OPEN, payload: { open: true } }),
    [dispatch]
  )

  const closeTriggersQuestionCheckModal = useCallback(
    () => dispatch({ type: SET_TRIGGERS_QUESTION_CHECK_MODAL_OPEN, payload: { open: false } }),
    [dispatch]
  )

  const openTriggersCategoryCheckModal = useCallback(
    index => dispatch({ type: SET_TRIGGERS_CATEGORY_CHECK_MODAL_OPEN, payload: { open: true, index } }),
    [dispatch]
  )

  const closeTriggersCategoryCheckModal = useCallback(
    () => dispatch({ type: SET_TRIGGERS_CATEGORY_CHECK_MODAL_OPEN, payload: { open: false } }),
    [dispatch]
  )

  return (
    <Provider
      state={state}
      actions={{
        randomIdAfterUndo,
        canUndo,
        handleUndo,
        handleChange,
        addQuestionToParent,
        addQuestionCloseTo,
        selectQuestion,
        handleSelectedQuestionChange,
        handleSelectedQuestionChangeOnPath,
        handleSelectedSegwareTestChange,
        setSelectedCategory,
        duplicateSelectedQuestion,
        removeSelectedQuestion,
        duplicateCategory,
        removeCategory,
        moveElementCloseTo,
        moveQuestionToParent,
        addCategory,
        addChildConditionsToSelectedQuestion,
        editChildConditionToSelectedQuestion,
        removeChildConditionToSelectedQuestion,
        openConditionsModal,
        closeConditionsModal,
        openTriggersQuestionCheckModal,
        closeTriggersQuestionCheckModal,
        openTriggersCategoryCheckModal,
        closeTriggersCategoryCheckModal
      }}
    >
      {children}
    </Provider>
  )
}

export default Form
